import { useState, useRef } from 'react';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import Button from '../../../../button';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import { Check, CrossIcon, DragIcon, PadLockIcon } from '../../../../../assets/icons';

export const VIVIER_COLUMNS_STORAGE_KEY = 'vivier_admin_columns';
export const VIVIER_COLUMNS_DEFAULT = ['contact', 'vivier'];

export const VIVIER_COLUMN_DEFINITIONS = [
  { key: 'contact', label: 'Contact' },
  { key: 'vivier', label: 'Vivier' },
  { key: 'licences', label: 'Permis' },
  { key: 'disponibilite', label: 'Disponibilité' },
  { key: 'departement', label: 'Département' },
  { key: 'source', label: 'Source candidat' },
  { key: 'cv', label: 'CV' },
];

const ModalCustomizeVivierColumns = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();

  const initialColumns = modalData?.activeColumns || VIVIER_COLUMNS_DEFAULT;
  const [activeColumns, setActiveColumns] = useState([...initialColumns]);
  const [isLoading, setIsLoading] = useState(false);

  const hasChanged =
    JSON.stringify(activeColumns) !== JSON.stringify(initialColumns);

  const dragIndex = useRef(null);

  const availableColumns = VIVIER_COLUMN_DEFINITIONS.filter(
    (col) => !activeColumns.includes(col.key)
  );

  const addColumn = (key) => {
    setActiveColumns((prev) => [...prev, key]);
  };

  const removeColumn = (key) => {
    setActiveColumns((prev) => prev.filter((k) => k !== key));
  };

  const handleDragStart = (index) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e, overIndex) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === overIndex) return;
    setActiveColumns((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current, 1);
      next.splice(overIndex, 0, moved);
      dragIndex.current = overIndex;
      return next;
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const handleConfirm = () => {
    setIsLoading(true);
    try {
      localStorage.setItem(VIVIER_COLUMNS_STORAGE_KEY, JSON.stringify(activeColumns));
      setModalDataReturn({ type: 'vivier_columns_updated', activeColumns: [...activeColumns] });
    } finally {
      setIsLoading(false);
      toggle();
    }
  };

  const handleCancel = () => {
    toggle();
  };

  const getColumnLabel = (key) =>
    VIVIER_COLUMN_DEFINITIONS.find((c) => c.key === key)?.label || key;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Personnaliser les colonnes
      </Modalheader>

      <ModalBody>
        <div className="flex gap-4 min-h-[300px] py-2">
          {/* Left panel: available columns */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-700 mb-3">Ajouter une colonne</p>
            <div className="flex flex-col gap-1">
              {availableColumns.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Toutes les colonnes sont affichées</p>
              ) : (
                availableColumns.map((col) => (
                  <div
                    key={col.key}
                    className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50"
                  >
                    <span className="text-sm text-gray-700">{col.label}</span>
                    <Button size="ICON" color="ICON" onClick={() => addColumn(col.key)}>
                      <Check wh={20} color="#111827" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="w-px bg-gray-200 flex-shrink-0" />

          {/* Right panel: active columns */}
          <div className="w-[280px] flex-shrink-0">
            <p className="font-medium text-gray-700 mb-3">Colonnes affichées</p>
            <div className="flex flex-col gap-1">
              {/* Locked "Candidat" row */}
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded">
                
              <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0">
                    <DragIcon wh={20} color="#9CA3AF" />
                  </span>
                  <span className="text-sm text-gray-400 truncate">
                    Candidat
                  </span>
                </div>
                <Button size="ICON" color="ICON">
                  <PadLockIcon wh={20} color="#9CA3AF" />
                </Button>
              </div>

              {/* Draggable active columns */}
              {activeColumns.map((key, index) => (
                <div
                  key={key}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded cursor-default"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="cursor-grab flex-shrink-0">
                      <DragIcon wh={20} color="#9CA3AF" />
                    </span>
                    <span className="text-sm text-gray-700 truncate">
                      {getColumnLabel(key)}
                    </span>
                  </div>
                  <Button size="ICON" color="ICON" onClick={() => removeColumn(key)}>
                    <CrossIcon wh={20} color="#111827" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={handleCancel}>
          Annuler
        </Button>
        {isLoading ? (
          <Button size="LG" color="DISABLED">
            Confirmation...
          </Button>
        ) : (
          <Button
            size="LG"
            color={hasChanged ? 'PRIMARY' : 'DISABLED'}
            onClick={hasChanged ? handleConfirm : undefined}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalCustomizeVivierColumns;
